// Decrypted item model + helpers (§11 typed catalog). The plaintext content lives only in
// memory after decryption; the server stores ciphertext only (§4, §19).
import { api, type ItemSummary, type ItemWriteBody } from "../api/client";
import { b64decode, b64encode, decryptItem, encryptItem, newWrappedItemKey, unwrapItemKey } from "../crypto/vault";
import { CATALOG, type FieldType, templateFor } from "./catalog";

export const ITEM_TYPES = CATALOG.map((t) => t.type);
export type ItemType = string;

export interface Field {
  key: string;
  label: string;
  type: FieldType;
  value: string;
  note?: string;
  secret?: boolean;
  multiline?: boolean;
}

export interface ItemContent {
  name: string;
  tags?: string[];
  notes?: string;
  fields: Field[];
}

export interface DecryptedItem {
  id: string;
  type: string;
  revision: number;
  folderId: string | null;
  content: ItemContent;
  offlineAllowed: boolean;
  updatedAt?: number; // epoch ms (server-set); undefined for older items without a timestamp
}

// A blank content with the type's template fields.
export function emptyContent(type: string): ItemContent {
  return {
    name: "",
    tags: [],
    notes: "",
    fields: templateFor(type).fields.map((t) => ({
      key: t.key,
      label: t.label,
      type: t.type,
      value: t.defaultValue ?? "",
      secret: t.secret,
      multiline: t.multiline,
    })),
  };
}

export type LegacyContent = { name?: string; host?: string; username?: string; password?: string; url?: string; notes?: string; fields?: Field[]; tags?: string[] };

// Normalize stored content to the field model, mapping the old flat shape forward.
export function normalizeContent(raw: LegacyContent): ItemContent {
  return normalize(raw);
}
function normalize(raw: LegacyContent): ItemContent {
  if (Array.isArray(raw.fields)) {
    return { name: raw.name ?? "", tags: raw.tags ?? [], notes: raw.notes ?? "", fields: raw.fields };
  }
  const fields: Field[] = [];
  const push = (key: string, label: string, type: FieldType, secret = false) => {
    const v = raw[key as keyof LegacyContent];
    if (typeof v === "string" && v) fields.push({ key, label, type, value: v, secret });
  };
  push("host", "Host", "host");
  push("url", "URL", "url");
  push("username", "Username", "username");
  push("password", "Password", "password", true);
  return { name: raw.name ?? "", tags: [], notes: raw.notes ?? "", fields };
}

function parseTs(s?: string): number | undefined {
  if (!s) return undefined;
  const ms = Date.parse(s);
  return Number.isNaN(ms) ? undefined : ms;
}

export function decryptSummary(orgKey: Uint8Array, s: ItemSummary): DecryptedItem {
  const itemKey = unwrapItemKey(orgKey, b64decode(s.wrapped_item_key));
  const raw = decryptItem<LegacyContent>(itemKey, b64decode(s.enc_content));
  return { id: s.id, type: s.type, revision: s.revision, folderId: s.folder_id ?? null, content: normalize(raw), offlineAllowed: s.offline_allowed ?? false, updatedAt: parseTs(s.updated_at) };
}

// Grid/column helpers — pull representative values from the field set.
export function displayHost(c: ItemContent): string {
  return c.fields.find((f) => ["host", "server_address", "mgmt_address", "endpoint_url", "url"].includes(f.key) && f.value)?.value ?? "";
}
export function displayUsername(c: ItemContent): string {
  return c.fields.find((f) => ["username", "key_id", "ssid"].includes(f.key) && f.value)?.value ?? "";
}
export function searchText(c: ItemContent): string {
  return [c.name, ...(c.tags ?? []), ...c.fields.filter((f) => !f.secret).map((f) => f.value)].join(" ").toLowerCase();
}

export async function loadItems(orgId: string, orgKey: Uint8Array): Promise<DecryptedItem[]> {
  const out: DecryptedItem[] = [];
  // Page through every item (the API caps a page at 200 and returns a cursor); a vault with
  // hundreds of items would otherwise show only the first page.
  let cursor: string | undefined;
  do {
    const { items, next_cursor } = await api.listItems(orgId, cursor);
    for (const s of items) {
      try {
        out.push(decryptSummary(orgKey, s));
      } catch {
        // A single undecryptable item should not break the whole list.
        out.push({ id: s.id, type: s.type, revision: s.revision, folderId: s.folder_id ?? null, content: { name: "⚠ cannot decrypt", fields: [] }, offlineAllowed: s.offline_allowed ?? false, updatedAt: parseTs(s.updated_at) });
      }
    }
    cursor = next_cursor;
  } while (cursor);
  return out;
}

export async function createItem(
  orgId: string,
  orgKey: Uint8Array,
  type: ItemType,
  content: ItemContent,
  folderId?: string | null,
  offlineAllowed = true,
): Promise<ItemSummary> {
  const { wrapped } = newWrappedItemKey(orgKey);
  // Encrypt with the same item_key we just wrapped.
  const itemKey = unwrapItemKey(orgKey, wrapped);
  const body: ItemWriteBody = {
    type,
    enc_content: b64encode(encryptItem(itemKey, content)),
    wrapped_item_key: b64encode(wrapped),
    folder_id: folderId ?? undefined,
    offline_allowed: offlineAllowed,
  };
  return api.createItem(orgId, body);
}

// --- Faceted search filter (UX phase 3) ---
// OR within a facet (any selected type/tag matches), AND across facets (type AND tag AND date AND
// text). Date facet keeps items modified within N days; items without a timestamp fail a date filter.
export interface ItemFilter {
  query: string;
  types: string[];
  tags: string[];
  modifiedWithinDays: number | null;
}

export const EMPTY_FILTER: ItemFilter = { query: "", types: [], tags: [], modifiedWithinDays: null };

export function filterIsActive(f: ItemFilter): boolean {
  return f.query.trim() !== "" || f.types.length > 0 || f.tags.length > 0 || f.modifiedWithinDays != null;
}

export function filterMatches(it: DecryptedItem, f: ItemFilter, nowMs: number): boolean {
  if (f.query && !searchText(it.content).includes(f.query.toLowerCase())) return false;
  if (f.types.length && !f.types.includes(it.type)) return false;
  if (f.tags.length) {
    const tags = it.content.tags ?? [];
    if (!f.tags.some((t) => tags.includes(t))) return false;
  }
  if (f.modifiedWithinDays != null) {
    if (it.updatedAt == null || it.updatedAt < nowMs - f.modifiedWithinDays * 86_400_000) return false;
  }
  return true;
}

/** Distinct tags across a set of items, sorted, for the tag-filter chips. */
export function allTags(items: DecryptedItem[]): string[] {
  const s = new Set<string>();
  for (const it of items) for (const t of it.content.tags ?? []) s.add(t);
  return [...s].sort((a, b) => a.localeCompare(b));
}

// Re-fetch one item (for its fresh wrapped key + revision), mutate its decrypted content, and save.
// Used by bulk edit so callers don't need to carry the wrapped key in the in-memory DecryptedItem.
// `mutate` returns the new content; pass `offlineAllowed` to also flip the offline flag (else kept).
export async function patchItem(
  orgKey: Uint8Array,
  id: string,
  mutate: (content: ItemContent) => ItemContent,
  offlineAllowed?: boolean,
): Promise<void> {
  const s = await api.getItem(id);
  const itemKey = unwrapItemKey(orgKey, b64decode(s.wrapped_item_key));
  const content = mutate(normalize(decryptItem<LegacyContent>(itemKey, b64decode(s.enc_content))));
  const body: ItemWriteBody = {
    type: s.type,
    enc_content: b64encode(encryptItem(itemKey, content)),
    wrapped_item_key: s.wrapped_item_key,
    revision: s.revision,
    offline_allowed: offlineAllowed ?? s.offline_allowed ?? true,
  };
  await api.updateItem(id, body);
}

export async function updateItem(
  item: DecryptedItem,
  orgKey: Uint8Array,
  wrappedItemKeyB64: string,
  content: ItemContent,
  offlineAllowed = true,
): Promise<ItemSummary> {
  // Re-encrypt with the existing item_key (content edit keeps the same key).
  const itemKey = unwrapItemKey(orgKey, b64decode(wrappedItemKeyB64));
  const body: ItemWriteBody = {
    type: item.type,
    enc_content: b64encode(encryptItem(itemKey, content)),
    wrapped_item_key: wrappedItemKeyB64,
    revision: item.revision,
    offline_allowed: offlineAllowed,
  };
  return api.updateItem(item.id, body);
}
